import {
  GetUnifiedAlarmGroupPayload,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';

/**
 * URL scheme for the Unified Monitoring grid, so a refresh or a shared link
 * restores the same alarm groups.
 *
 *   rg / scan / ent  legacy (v2) ids, bucketed by product
 *   unified          v3 ids (RG or enterprise) — membership here is itself
 *                    the v3 signal, independent of product
 *   pk               `id:partitionKey` pairs, so by-id lookups hit the right
 *                    DB partition directly instead of scanning
 *
 * A v3 id appearing in both its product bucket and `unified` is tolerated
 * permanently: such links sit in open tabs.
 */
const RG_PARAM = 'rg';
const SCAN_PARAM = 'scan';
const ENT_PARAM = 'ent';
const UNIFIED_PARAM = 'unified';
const PARTITION_KEY_PARAM = 'pk';

const splitIds = (searchParams: URLSearchParams, key: string): string[] =>
  searchParams.get(key)?.split(',').filter(Boolean) ?? [];

function parsePartitionKeys(
  searchParams: URLSearchParams
): Map<string, string> {
  const byId = new Map<string, string>();
  splitIds(searchParams, PARTITION_KEY_PARAM).forEach((pair) => {
    const separator = pair.indexOf(':');
    if (separator > 0) {
      byId.set(pair.slice(0, separator), pair.slice(separator + 1));
    }
  });
  return byId;
}

/** True when the URL names at least one alarm group to restore. */
export function hasAlarmGroupParams(searchParams: URLSearchParams): boolean {
  return (
    [RG_PARAM, SCAN_PARAM, ENT_PARAM, UNIFIED_PARAM].filter(
      (key) => splitIds(searchParams, key).length > 0
    ).length > 0
  );
}

/**
 * Turns the URL into the by-id lookup payloads sent over the monitoring socket.
 * A `unified` id wins over any product bucket it also appears in, so
 * `?rg=x&unified=x` resolves `x` once, as v3.
 */
export function parseAlarmGroupPayloads(
  searchParams: URLSearchParams
): GetUnifiedAlarmGroupPayload[] {
  const unifiedIds = splitIds(searchParams, UNIFIED_PARAM);
  const unified = new Set(unifiedIds);
  const partitionKeys = parsePartitionKeys(searchParams);

  const legacy = (key: string) =>
    splitIds(searchParams, key).filter((id) => !unified.has(id));

  return [
    ...unifiedIds.map((alarmGroupId) => ({
      alarmGroupId,
      // Placeholder -- the backend re-derives the real product itself.
      product: UnifiedMonitoringProduct.REMOTE_GUARDING,
      unificationEnabled: true,
    })),
    ...legacy(RG_PARAM).map((alarmGroupId) => ({
      alarmGroupId,
      product: UnifiedMonitoringProduct.REMOTE_GUARDING,
      unificationEnabled: false,
    })),
    ...legacy(SCAN_PARAM).map((alarmGroupId) => ({
      alarmGroupId,
      product: UnifiedMonitoringProduct.SCAN,
      unificationEnabled: false,
    })),
    ...legacy(ENT_PARAM).map((alarmGroupId) => ({
      alarmGroupId,
      product: UnifiedMonitoringProduct.ENTERPRISE,
      unificationEnabled: false,
    })),
  ].map(({ alarmGroupId, product, unificationEnabled }) => ({
    alarm_group_id: alarmGroupId,
    product,
    // Omitted rather than `false` for v2, so a legacy request is unchanged.
    unification_enabled: unificationEnabled || undefined,
    partition_key: unificationEnabled
      ? partitionKeys.get(alarmGroupId)
      : undefined,
  }));
}

/**
 * Rebuilds the URL from the groups in the grid: unification-enabled groups go
 * to `unified` regardless of product, legacy ones to their product bucket.
 */
export function buildAlarmGroupParams(
  searchParams: URLSearchParams,
  alarmGroups: UnifiedAlarmGroup[]
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  const values: Record<string, string[]> = {
    [RG_PARAM]: [],
    [SCAN_PARAM]: [],
    [ENT_PARAM]: [],
    [UNIFIED_PARAM]: [],
    [PARTITION_KEY_PARAM]: [],
  };

  alarmGroups.forEach((group) => {
    const isRemoteGuarding =
      group.product === undefined ||
      group.product === UnifiedMonitoringProduct.REMOTE_GUARDING;

    // RG/Enterprise share a fetch path and stay in `unified`; Scan doesn't.
    if (
      group.unificationEnabled &&
      group.product !== UnifiedMonitoringProduct.SCAN
    ) {
      values[UNIFIED_PARAM].push(group.id);
    } else if (group.product === UnifiedMonitoringProduct.ENTERPRISE) {
      values[ENT_PARAM].push(group.id);
    } else if (isRemoteGuarding) {
      values[RG_PARAM].push(group.id);
    } else {
      values[SCAN_PARAM].push(group.id);
    }

    if (group.unificationEnabled && group.partition_key) {
      values[PARTITION_KEY_PARAM].push(`${group.id}:${group.partition_key}`);
    }
  });

  // Delete rather than skip when empty: an id can move between params, and a
  // leftover `rg=` would resurrect it on the next refresh.
  Object.entries(values).forEach(([key, ids]) =>
    ids.length > 0 ? next.set(key, ids.join(',')) : next.delete(key)
  );

  return next;
}
